<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Card, Table, TableHead, TableHeadCell, TableBody, TableBodyCell,
		Button, Modal, Label, Input, Toggle, Badge, Spinner
	} from 'flowbite-svelte';
	import PageHeader from '../../components/PageHeader.svelte';
	import RowAction from '../../components/admin/RowAction.svelte';
	import {
		listServiceGroups, createServiceGroup, updateServiceGroup, deleteServiceGroup, reorderServiceGroups
	} from '../../AppNetwork';
	import type { ServiceGroup } from '../../AppTypes';

	let groups: ServiceGroup[] = [];
	let loading = true;
	let error = '';

	// Drag-and-drop reordering
	let dragIndex: number | null = null;
	let overIndex: number | null = null;

	function onDragStart(i: number) {
		dragIndex = i;
	}
	function onDragOver(e: DragEvent, i: number) {
		e.preventDefault();
		overIndex = i;
	}
	function onDragLeave(i: number) {
		if (overIndex === i) overIndex = null;
	}
	function onDragEnd() {
		dragIndex = null;
		overIndex = null;
	}
	async function onDrop(e: DragEvent, i: number) {
		e.preventDefault();
		const from = dragIndex;
		dragIndex = null;
		overIndex = null;
		if (from === null || from === i) return;
		const next = [...groups];
		const [moved] = next.splice(from, 1);
		next.splice(i, 0, moved);
		next.forEach((g, idx) => (g.displayOrder = idx));
		groups = next;
		try {
			await reorderServiceGroups(groups.map((g) => g.id));
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to save new order';
			await load();
		}
	}

	let showModal = false;
	let editing: ServiceGroup | null = null;
	let form = blankForm();
	let isSaving = false;

	function blankForm() {
		return { name: '', slug: '', description: '', displayOrder: 0, isPublic: true };
	}

	async function load() {
		loading = true;
		error = '';
		try {
			groups = await listServiceGroups();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load groups';
		} finally {
			loading = false;
		}
	}
	onMount(load);

	function openCreate() {
		editing = null; form = blankForm(); error = ''; showModal = true;
	}
	function openEdit(group: ServiceGroup) {
		editing = group; error = '';
		form = {
			name: group.name, slug: group.slug, description: group.description ?? '',
			displayOrder: group.displayOrder, isPublic: group.isPublic
		};
		showModal = true;
	}

	async function save() {
		isSaving = true;
		error = '';
		try {
			if (editing) await updateServiceGroup(editing.id, form);
			else await createServiceGroup(form);
			showModal = false;
			await load();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to save group';
		}
		isSaving = false;
	}

	async function remove(group: ServiceGroup) {
		if (!confirm(`Delete group "${group.name}"? Services in it become ungrouped.`)) return;
		await deleteServiceGroup(group.id);
		await load();
	}
</script>

<PageHeader title="Groups" description="Categories that group services on the public status page." />

<div class="space-y-6">
	<Card class="max-w-none">
		<div class="flex justify-end">
			<Button on:click={openCreate}><i class="fa-solid fa-plus me-2"></i>New Group</Button>
		</div>
	</Card>

	<Card padding="none" class="max-w-none">
		{#if loading}
			<div class="p-8 text-center">
				<i class="fa-solid fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
				<p class="text-gray-500 dark:text-gray-400">Loading groups...</p>
			</div>
		{:else if error}
			<div class="p-8 text-center">
				<i class="fa-solid fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">{error}</p>
				<Button class="mt-4" on:click={load}>Retry</Button>
			</div>
		{:else if groups.length === 0}
			<div class="p-8 text-center">
				<i class="fa-solid fa-layer-group text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">No groups yet</p>
			</div>
		{:else}
			<Table striped divClass="relative overflow-x-auto rounded-lg">
				<TableHead class="bg-gray-50 dark:bg-gray-700">
					<TableHeadCell class="w-8"><span class="sr-only">Reorder</span></TableHeadCell>
					<TableHeadCell>Name</TableHeadCell>
					<TableHeadCell>Slug</TableHeadCell>
					<TableHeadCell>Order</TableHeadCell>
					<TableHeadCell>Public</TableHeadCell>
					<TableHeadCell>Actions</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each groups as group, i (group.id)}
						<tr
							class="border-b dark:border-gray-700 odd:bg-white even:bg-gray-50 odd:dark:bg-gray-800 even:dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 {overIndex === i ? 'border-t-2 border-t-primary-500' : ''} {dragIndex === i ? 'opacity-40' : ''}"
							on:dragover={(e) => onDragOver(e, i)}
							on:drop={(e) => onDrop(e, i)}
							on:dragleave={() => onDragLeave(i)}
						>
							<TableBodyCell class="pr-0">
								<span
									class="cursor-move text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
									draggable="true"
									role="button"
									tabindex="-1"
									aria-label="Drag to reorder"
									title="Drag to reorder"
									on:dragstart={() => onDragStart(i)}
									on:dragend={onDragEnd}
								>
									<i class="fa-solid fa-grip-vertical"></i>
								</span>
							</TableBodyCell>
							<TableBodyCell class="font-medium text-gray-900 dark:text-white">{group.name}</TableBodyCell>
							<TableBodyCell class="text-gray-500 dark:text-gray-400 font-mono">{group.slug}</TableBodyCell>
							<TableBodyCell class="text-gray-500 dark:text-gray-400">{group.displayOrder}</TableBodyCell>
							<TableBodyCell>
								{#if group.isPublic}<Badge color="green">Yes</Badge>{:else}<Badge color="dark">No</Badge>{/if}
							</TableBodyCell>
							<TableBodyCell>
								<div class="flex gap-1">
									<RowAction icon="fa-pen" label="Edit" on:click={() => openEdit(group)} />
									<RowAction icon="fa-trash" label="Delete" danger on:click={() => remove(group)} />
								</div>
							</TableBodyCell>
						</tr>
					{/each}
				</TableBody>
			</Table>
		{/if}
	</Card>
</div>

<Modal bind:open={showModal} title={editing ? 'Edit Group' : 'New Group'} size="md">
	<div class="space-y-4">
		<div><Label class="mb-1">Name</Label><Input bind:value={form.name} placeholder="Core Services" /></div>
		<div><Label class="mb-1">Slug</Label><Input bind:value={form.slug} placeholder="core" /></div>
		<div><Label class="mb-1">Description</Label><Input bind:value={form.description} /></div>
		<div class="grid grid-cols-2 gap-4">
			<div><Label class="mb-1">Display order</Label><Input type="number" bind:value={form.displayOrder} /></div>
			<div class="flex items-end"><Toggle bind:checked={form.isPublic}>Public</Toggle></div>
		</div>
		{#if error}<p class="text-red-500 text-sm">{error}</p>{/if}
	</div>
	<svelte:fragment slot="footer">
		<div class="flex gap-2">
			<Button color="primary" on:click={save} disabled={isSaving || !form.name || !form.slug}>
				{#if isSaving}<Spinner size="sm" class="mr-2" />{/if}Save
			</Button>
			<Button color="alternative" on:click={() => (showModal = false)}>Cancel</Button>
		</div>
	</svelte:fragment>
</Modal>
